import React, { useState } from 'react';
import DocumentMeta from 'react-document-meta';
import { Link } from 'react-router-dom';
import ImageStrip from '../components/ImageStrip';
import ProductInfo from '../components/ProductInfo';
import SourdoughSection from '../components/SourdoughSection';
import SourdoughCta from '../components/SourdoughCta';
import breadImages from '../data/breadImages';
import { useI18n } from '../i18n/I18nContext';
import styles from '../styles/ArtisanBread.module.css';

const publicUrl = process.env.PUBLIC_URL || '';

const featuredImages = breadImages.slice(4, 10).map(image => ({
  ...image,
  src: `${publicUrl}/images/${image.file}`,
}));

const tabs = [
  { id: 'wholegrain', key: 'wholegrain' },
  { id: 'white', key: 'white' },
  { id: 'goldenGrain', key: 'goldenGrain' },
  { id: 'dinkel', key: 'dinkel' },
  { id: 'mixes', key: 'mixes' },
  { id: 'dinkelRye', key: 'dinkelRye' },
  { id: 'kozunak', key: 'kozunak' },
];

const ArtisanBread = () => {
  const { language, t } = useI18n();
  const [activeTab, setActiveTab] = useState('wholegrain');
  const [selectedBread, setSelectedBread] = useState(null);
  const title = t('pages.artisanBreads.title');
  const description = t('pages.artisanBreads.intro');
  const meta = {
    title,
    description,
    meta: {
      name: {
        keywords: 'homemade artisan bread, artisan bread, homemade pastries, fresh pastries, bread catering, bakery Sofia, Grain Bites',
        robots: 'index, follow',
      },
      property: {
        'og:type': 'website',
        'og:title': title,
        'og:description': description,
        'og:image': `${publicUrl}/images/breads/dinkel-bread1.webp`,
        'twitter:card': 'summary_large_image',
      },
    },
  };
  const visibleImages = breadImages.filter(image => (
    image.file !== 'breads/testo.webp' && image.categories.includes(activeTab)
  ));

  return (
    <DocumentMeta {...meta} extend>
      <main className={styles.page}>
        <section
          className={styles.hero}
          style={{ backgroundImage: `url(${publicUrl}/images/hero/artisan-bread-bg.webp)` }}
        >
          <p className={styles.eyebrow}>{t('pages.artisanBreads.eyebrow')}</p>
          <h1>{title}</h1>
          <p>{description}</p>
          <p className={styles.tagline}>{t('pages.artisanBreads.tagline')}</p>
        </section>

        <ImageStrip
          eyebrow={t('imageStrip.breadTable')}
          items={featuredImages}
          ariaLabel="Featured artisan bread images"
        />

        <div className={styles.contentHeading}>
          <p className={styles.label}>{t('pages.artisanBreads.sectionLabel')}</p>
          <h2>{t('pages.artisanBreads.heading')}</h2>
        </div>

        <section className={styles.content}>
          <div className={styles.imagePanel}>
            <img
              src={`${publicUrl}/images/breads/dinkel-bread1.webp`}
              alt="Fresh artisan bread"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className={styles.copy}>
            <p>{t('pages.artisanBreads.description')}</p>

            {language && (
              <div className={styles.article}>
                <h3>{t('pages.artisanBreads.sourdoughTitle')}</h3>
                <p>{t('pages.artisanBreads.sourdoughIntro')}</p>
              </div>
            )}

            <div className={styles.facts}>
              <div>
                <strong>{t('pages.artisanBreads.slowPreparation')}</strong>
                <span>{t('pages.artisanBreads.slowPreparationText')}</span>
              </div>
              <div>
                <strong>{t('pages.artisanBreads.madeForSharing')}</strong>
                <span>{t('pages.artisanBreads.madeForSharingText')}</span>
              </div>
            </div>

            <Link className={styles.button} to={`${publicUrl}/Contact`}>
              {t('pages.artisanBreads.planOrder')}
            </Link>
          </div>
        </section>

        {language && (
          <SourdoughSection
            label={t('pages.artisanBreads.sourdoughLabel')}
            title={t('pages.artisanBreads.sourdoughWhatTitle')}
            text={t('pages.artisanBreads.sourdoughWhatText')}
            benefitsTitle={t('pages.artisanBreads.sourdoughBenefitsTitle')}
            benefits={t('pages.artisanBreads.sourdoughBenefits')}
            imageSrc={`${publicUrl}/images/breads/testo.webp`}
            imageAlt="Frisches Sauerteigbrot"
          />
        )}

        <SourdoughCta
          title={t('pages.artisanBreads.sourdoughCtaTitle')}
          text={t('pages.artisanBreads.sourdoughCtaText')}
          linkLabel={t('pages.artisanBreads.sourdoughCtaLink')}
          to={`${publicUrl}/Sourdough`}
        />

        <section className={styles.gallery} aria-labelledby="bread-gallery-title">
          <div className={styles.galleryHead}>
            <p className={styles.label}>{t('pages.artisanBreads.collection')}</p>
            <h2 id="bread-gallery-title">{t('pages.artisanBreads.galleryTitle')}</h2>
          </div>

          <nav className={styles.tabs} aria-label="Bread categories">
            {tabs.map(tab => (
              <button
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                key={tab.id}
                type="button"
                aria-pressed={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {t(`pages.artisanBreads.categories.${tab.key}`)}
              </button>
            ))}
          </nav>

          <div className={styles.grid}>
            {visibleImages.map(image => (
              <button
                className={styles.card}
                key={image.file}
                type="button"
                onClick={() => setSelectedBread(image)}
                aria-label={`${t('pages.artisanBreads.viewDetails')}: ${image.title}`}
              >
                <span className={styles.cardImage}>
                  <img
                    src={`${publicUrl}/images/${image.file}`}
                    alt={image.title}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className={styles.overlay} aria-hidden="true">
                    {t('pages.artisanBreads.viewDetails')}
                  </span>
                </span>
                <span className={styles.caption}>{image.title}</span>
              </button>
            ))}
          </div>
        </section>

        {selectedBread && (
          <ProductInfo
            product={selectedBread}
            categoryLabel={t(`pages.artisanBreads.categories.${selectedBread.categoryKey}`)}
            imageSrc={`${publicUrl}/images/${selectedBread.file}`}
            labels={{
              close: t('pages.artisanBreads.close'),
              ingredients: t('pages.artisanBreads.ingredients'),
            }}
            onClose={() => setSelectedBread(null)}
          />
        )}
      </main>
    </DocumentMeta>
  );
};

export default ArtisanBread;
